/**
 * Content Preview Modal Component
 * Carbon Design System modal for content preview with visitor context simulation
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Grid,
  Column,
  Select,
  SelectItem,
  Toggle,
  Button,
  Tag,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Loading,
  InlineNotification,
  Tile,
} from '@carbon/react';
import { Devices, View, User, Settings, Launch } from '@carbon/icons-react';

import type { ContentPreviewOptions, VisitorContextPreview } from '../../../shared/interfaces/ContentManagementAdmin';

interface ContentPreviewModalProps {
  open: boolean;
  onClose: () => void;
  previewOptions: ContentPreviewOptions;
}

const ContentPreviewModal: React.FC<ContentPreviewModalProps> = ({ open, onClose, previewOptions }) => {
  const [loading, setLoading] = useState(false);
  const [previewContent, setPreviewContent] = useState<any>(null);
  const [deviceType, setDeviceType] = useState<'desktop' | 'tablet' | 'mobile'>(previewOptions.deviceType || 'desktop');
  const [visitorContext, setVisitorContext] = useState<VisitorContextPreview>({
    visitorId: 'preview-user',
    role: 'educator',
    institutionType: 'high_school',
    location: {
      country: 'US',
      region: 'California',
      city: 'San Francisco',
    },
    behavior: {
      engagementScore: 75,
      sessionDuration: 300,
      pageViews: 5,
    },
  });

  useEffect(() => {
    if (open) {
      loadPreviewContent();
    }
  }, [open, previewOptions, deviceType, visitorContext]);

  const loadPreviewContent = async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams({
        preview: previewOptions.previewMode,
        device: deviceType,
        visitor_context: JSON.stringify(visitorContext),
      });

      const response = await fetch(`/api/content-management/content/${previewOptions.contentId}/preview?${params}`);

      if (!response.ok) {
        throw new Error('Failed to load preview content');
      }

      const data = await response.json();
      setPreviewContent(data.content);
    } catch (error) {
      console.error('Preview load error:', error);
      setPreviewContent(null);
    } finally {
      setLoading(false);
    }
  };

  const handleVisitorContextChange = (field: string, value: any) => {
    setVisitorContext(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const getDeviceStyles = () => {
    switch (deviceType) {
      case 'mobile':
        return {
          width: '375px',
          height: '667px',
          margin: '0 auto',
          border: '8px solid #000',
          borderRadius: '25px',
        };
      case 'tablet':
        return {
          width: '768px',
          height: '1024px',
          margin: '0 auto',
          border: '4px solid #333',
          borderRadius: '15px',
        };
      default:
        return {
          width: '100%',
          height: '600px',
          border: '1px solid var(--cds-border-subtle)',
        };
    }
  };

  const renderPreviewControls = () => (
    <Grid>
      <Column lg={4} md={2} sm={2}>
        <Select
          id="device-type"
          labelText="Device Type"
          value={deviceType}
          onChange={e => setDeviceType(e.target.value as any)}
        >
          <SelectItem value="desktop" text="Desktop" />
          <SelectItem value="tablet" text="Tablet" />
          <SelectItem value="mobile" text="Mobile" />
        </Select>
      </Column>

      <Column lg={4} md={2} sm={2}>
        <Select
          id="visitor-role"
          labelText="Visitor Role"
          value={visitorContext.role}
          onChange={e => handleVisitorContextChange('role', e.target.value)}
        >
          <SelectItem value="educator" text="Educator" />
          <SelectItem value="administrator" text="Administrator" />
          <SelectItem value="parent" text="Parent" />
          <SelectItem value="student" text="Student" />
          <SelectItem value="unknown" text="Unknown" />
        </Select>
      </Column>

      <Column lg={4} md={2} sm={2}>
        <Select
          id="institution-type"
          labelText="Institution Type"
          value={visitorContext.institutionType}
          onChange={e => handleVisitorContextChange('institutionType', e.target.value)}
        >
          <SelectItem value="elementary" text="Elementary" />
          <SelectItem value="middle_school" text="Middle School" />
          <SelectItem value="high_school" text="High School" />
          <SelectItem value="district" text="District" />
          <SelectItem value="private_school" text="Private School" />
        </Select>
      </Column>

      <Column lg={4} md={4} sm={2}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '1.5rem' }}>
          <Tag type="blue">
            <User size={12} style={{ marginRight: '0.25rem' }} />
            {visitorContext.role}
          </Tag>
          <Tag type="cool-gray">{deviceType}</Tag>
          <Tag type="green">{previewOptions.previewMode}</Tag>
        </div>
      </Column>
    </Grid>
  );

  const renderPreviewContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <Loading description="Loading preview..." />
        </div>
      );
    }

    if (!previewContent) {
      return (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <InlineNotification
            kind="warning"
            title="Preview Not Available"
            subtitle="Unable to load preview content"
            lowContrast
          />
        </div>
      );
    }

    return (
      <div
        style={{
          ...getDeviceStyles(),
          backgroundColor: '#fff',
          overflow: 'auto',
          position: 'relative',
        }}
      >
        {/* Mock content preview */}
        <div style={{ padding: '1rem' }}>
          <h2>{previewContent.title || 'Content Preview'}</h2>
          <p>{previewContent.description || 'Content description would appear here'}</p>

          {previewContent.sections?.map((section: any, index: number) => (
            <div key={section.id || index} style={{ marginBottom: '1.5rem' }}>
              <h3>{section.title}</h3>
              <div>{section.content}</div>
            </div>
          ))}

          {/* Personalization indicator */}
          {visitorContext.role !== 'unknown' && (
            <div
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(0, 123, 255, 0.1)',
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid rgba(0, 123, 255, 0.3)',
              }}
            >
              <Tag type="blue" size="sm">
                Personalized for {visitorContext.role}
              </Tag>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      modalHeading="Content Preview"
      modalLabel="Content Management"
      size="lg"
      hasScrollingContent
      primaryButtonText="Open in New Tab"
      secondaryButtonText="Close"
      onRequestSubmit={() => {
        window.open(`/preview/${previewOptions.contentId}`, '_blank');
      }}
    >
      <Tabs>
        <TabList>
          <Tab renderIcon={View}>Preview</Tab>
          <Tab renderIcon={Settings}>Context</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <div style={{ marginBottom: '1rem' }}>{renderPreviewControls()}</div>
            <div style={{ marginBottom: '1rem' }}>{renderPreviewContent()}</div>
          </TabPanel>

          <TabPanel>
            <div>
              <h4 style={{ marginBottom: '1rem' }}>Visitor Context Configuration</h4>
              <Tile>
                <Grid>
                  <Column lg={8} md={4} sm={2}>
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Location:</strong> {visitorContext.location?.city}, {visitorContext.location?.region},{' '}
                      {visitorContext.location?.country}
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Engagement Score:</strong> {visitorContext.behavior?.engagementScore}/100
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Session Duration:</strong> {visitorContext.behavior?.sessionDuration}s
                    </div>
                    <div>
                      <strong>Page Views:</strong> {visitorContext.behavior?.pageViews}
                    </div>
                  </Column>
                </Grid>
              </Tile>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Modal>
  );
};

export default ContentPreviewModal;
